import { OptImg } from "@/components/OptImg";

type ProductReview = {
  name: string;
  text: string;
  rating?: number | null;
  location?: string | null;
};

type ProductCardProps = {
  name: string;
  image?: string | null;
  imageAlt?: string | null;
  url: string;
  price?: string | null;
  rating?: number | null;
  reviewsCount?: number | null;
  features?: string[] | null;
  ctaLabel?: string | null;
  ctaBgColor?: string | null;
  ctaTextColor?: string | null;
  marketplace?: string | null;
  reviews?: ProductReview[] | null;
  variant?: "full" | "compact";
};

function formatRating(r: number) {
  return r.toFixed(1).replace(".", ",");
}

function StarRow({ value }: { value: number }) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  const stars = Array.from({ length: 5 }, (_, i) => {
    if (i < full) return "★";
    if (i === full && half) return "⯨";
    return "☆";
  });
  return (
    <span aria-hidden className="tracking-[0.15em] text-amber-500">
      {stars.join("")}
    </span>
  );
}

export function ProductCard({
  name,
  image,
  imageAlt,
  url,
  price,
  rating,
  reviewsCount,
  features,
  ctaLabel,
  ctaBgColor,
  ctaTextColor,
  marketplace,
  reviews,
}: ProductCardProps) {
  const cta = ctaLabel?.trim() || (marketplace ? `Comprar na ${marketplace}` : "Ver oferta agora");
  const showRating = typeof rating === "number" && rating > 0;
  const showReviews = typeof reviewsCount === "number" && reviewsCount > 0;
  const feats = (features ?? []).filter(Boolean).slice(0, 6);
  const revs = (reviews ?? []).filter((r) => r && r.text).slice(0, 4);

  const ctaBg = ctaBgColor?.trim() || "#FF6A00";
  const ctaText = ctaTextColor?.trim() || "#FFFFFF";

  return (
    <aside
      className="not-prose group relative my-10 overflow-hidden rounded-3xl border border-zinc-200 bg-white text-zinc-900 shadow-[0_25px_60px_-15px_rgba(15,23,42,0.25),0_8px_20px_-8px_rgba(15,23,42,0.15)] ring-1 ring-black/5"
      aria-label={`Produto recomendado: ${name}`}
    >
      {/* ribbon */}
      <div
        className="absolute -right-12 top-5 z-10 rotate-45 px-12 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] shadow-lg"
        style={{ background: ctaBg, color: ctaText }}
        aria-hidden
      >
        Mais vendido
      </div>

      <div className="relative grid gap-6 p-6 md:grid-cols-[260px_1fr] md:items-start md:p-8">
        {image ? (
          <div className="relative overflow-hidden rounded-2xl bg-zinc-50 ring-1 ring-zinc-200">
            <OptImg
              src={image}
              alt={imageAlt || name}
              width={520}
              height={520}
              widths={[240, 320, 480, 640]}
              sizes="(max-width: 768px) 80vw, 260px"
              className="aspect-square w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
            />
            {price && (
              <div className="absolute bottom-2 left-2 rounded-full bg-zinc-900/90 px-3 py-1 text-[11px] font-bold text-white backdrop-blur">
                {price}
              </div>
            )}
          </div>
        ) : (
          <div className="aspect-square w-full rounded-2xl bg-zinc-100" aria-hidden />
        )}

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] shadow"
              style={{ background: ctaBg, color: ctaText }}
            >
              ★ Escolha do editor
            </span>
            {marketplace && (
              <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-semibold text-zinc-600">
                Disponível na {marketplace}
              </span>
            )}
          </div>

          <h3 className="text-2xl font-extrabold leading-tight text-zinc-900 md:text-3xl">{name}</h3>

          {(showRating || showReviews) && (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {showRating && (
                <>
                  <StarRow value={rating!} />
                  <span className="font-bold text-zinc-900">{formatRating(rating!)}/5</span>
                </>
              )}
              {showReviews && (
                <span className="text-zinc-600">
                  com base em <strong className="text-zinc-900">{reviewsCount!.toLocaleString("pt-BR")}</strong> avaliações de clientes
                </span>
              )}
            </div>
          )}

          {feats.length > 0 && (
            <ul className="grid gap-1.5 text-sm text-zinc-800 sm:grid-cols-2">
              {feats.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <span
                    className="mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-black"
                    style={{ background: ctaBg, color: ctaText }}
                    aria-hidden
                  >
                    ✓
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-end justify-between gap-4 border-t border-zinc-200 pt-4">
            {price ? (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500">A partir de</p>
                <p className="text-3xl font-black leading-none text-zinc-900">{price}</p>
              </div>
            ) : <span />}
            <a
              href={url}
              target="_blank"
              rel="sponsored nofollow noopener"
              aria-label={`${cta} — ${name} (link de afiliado)`}
              style={{
                background: `linear-gradient(135deg, ${ctaBg}, color-mix(in oklab, ${ctaBg} 70%, #000))`,
                color: ctaText,
                boxShadow: `0 14px 30px -10px color-mix(in oklab, ${ctaBg} 60%, transparent)`,
              }}
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-extrabold uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5 hover:scale-[1.02] focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              {cta}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            </a>
          </div>

          {revs.length > 0 && (
            <div className="space-y-3 rounded-2xl border border-zinc-200 bg-zinc-50/60 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-extrabold uppercase tracking-wider text-zinc-700">
                  💬 O que os clientes dizem
                </p>
                {showRating && (
                  <span className="text-xs font-bold text-amber-600">★ {formatRating(rating!)}/5</span>
                )}
              </div>
              <ul className="grid gap-3 sm:grid-cols-2">
                {revs.map((r, i) => (
                  <li key={i} className="rounded-xl border border-zinc-200 bg-white p-3 shadow-sm">
                    <StarRow value={r.rating ?? 5} />
                    <p className="mt-1.5 text-[13px] leading-snug text-zinc-700">"{r.text}"</p>
                    <p className="mt-2 text-[11px] font-semibold text-zinc-900">
                      {r.name}
                      {r.location ? <span className="font-normal text-zinc-500"> — {r.location}</span> : null}
                      <span className="ml-1 text-emerald-600">· Compra verificada</span>
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-[11px] text-zinc-500">
            🔒 Link de afiliado oficial. Você nos apoia sem custo adicional ao comprar pelo nosso link.
          </p>
        </div>
      </div>
    </aside>
  );
}
